import { action, computed, observable } from 'mobx';
import last from 'lodash/last';
import { IRecurringScheduleBlockResponse } from '~/types/server.types';
import { ContainerableBlock } from '~/models/blocks/containerable-block.model';
import { PaymentBlock } from '~/models/blocks/payment-block.model';
import {
    IRecurringPayment,
    IRecurringSchedule,
    PaymentStatus
} from '~/types/model.types';
import type { FlowRevision } from '~/models/flow-revision.model';
import type { RootStore } from '~/stores/root.store';
import { formatDate } from '~/utils/date.utils';
import { TBlock } from '~/types/builder.types';

type RecurringScheduleBlockDataForServer = ReturnType<
    ContainerableBlock['getDataForServer']
> &
    Pick<
        IRecurringScheduleBlockResponse,
        | 'type'
        | 'recurring_schedule'
        | 'sent_invoices_count'
        | 'upcoming_invoices_count'
        | 'paid_payments_count'
        | 'total_paid_amount'
        | 'last_invoice_date'
    >;

export class RecurringScheduleBlock extends ContainerableBlock {
    type: 'recurring_schedule_block';
    @observable recurring_payments: boolean;
    @observable sent_invoices_count: number;
    @observable upcoming_invoices_count: number;
    @observable paid_payments_count: number;
    @observable total_paid_amount: number;
    @observable last_invoice_date: string;
    @observable recurring_schedule: IRecurringSchedule;
    @observable upcoming_invoices: IRecurringPayment[];
    @observable upcoming_invoice_due_date: string;
    @observable min_valid_due_date: string;
    @observable current_payment_block_id: string;

    constructor(
        block: IRecurringScheduleBlockResponse,
        flowRevision: FlowRevision<RootStore>,
        rootStore: RootStore
    ) {
        super(block, flowRevision, rootStore);
        this.type = block.type;
        this.recurring_schedule = block.recurring_schedule;
        this.upcoming_invoices = block.upcoming_invoices;
    }

    get hoverable(): boolean {
        const {
            userStore: { currUser }
        } = this.rootStore;

        return !currUser.isSimplifiedInvoiceEnabled;
    }

    get isDeletable() {
        return false;
    }

    get isRecurringInvoice() {
        return true;
    }

    @computed
    get lastPaymentBlock() {
        return last(this.childBlocks) as PaymentBlock;
    }

    @computed
    get upcomingPaymentBlock() {
        return this.flowRevision.upcomingPayment;
    }

    @computed
    get upcomingPaymentBlockNonOverdue() {
        return this.flowRevision.upcomingPaymentNonOverdue;
    }

    @computed
    get upcomingInvoices() {
        return this.upcoming_invoices;
    }

    @computed
    get currentPaymentBlock() {
        return !!this.current_payment_block_id
            ? this.paymentBlockById(this.current_payment_block_id)
            : this.lastPaymentBlock;
    }

    @computed
    get paymentBlocks() {
        return this.childBlocks as PaymentBlock[];
    }

    canBeParentOf(block: TBlock): boolean {
        return block.type === 'payment_block';
    }

    @computed
    get totalPaid() {
        return this.total_paid_amount || 0;
    }

    @computed
    get isLastRecurringPaymentOverdue() {
        return this.lastPaymentBlock.paymentStatus === PaymentStatus.Overdue;
    }

    private paymentBlockById(paymentBlockId: string) {
        return (this.childBlocks as PaymentBlock[])?.find(
            child => child.block_id === paymentBlockId
        ) as PaymentBlock;
    }

    @computed
    get nextPaymentBlock() {
        const nextPaymentBlock = this.childBlocks.find(
            (payment: PaymentBlock) => {
                return !payment.isPaid && !payment.isRefunded;
            }
        );
        return nextPaymentBlock as PaymentBlock;
    }

    @computed
    get isPaymentHandled() {
        return this.lastPaymentBlock.isPaymentHandled;
    }

    @computed
    get isActive() {
        return this.connectedVariable?.value?.is_active;
    }

    @computed
    get isDeleteDisabled() {
        return this.paid_payments_count > 0;
    }

    isDraggable() {
        return false;
    }

    dndAcceptedTypes() {
        return null;
    }

    @computed
    get errors() {
        const {
            builderUiStore: { showErrors }
        } = this.rootStore;

        if (!showErrors) {
            return {};
        }

        const errors: any = {};

        const startDate = new Date(
            formatDate(
                this.flow.isPublished
                    ? this.upcoming_invoice_due_date
                    : this.recurring_schedule.first_invoice_due_date,
                'YYYY-MM-dd'
            )
        );
        const today = new Date(formatDate(new Date(), 'YYYY-MM-dd'));
        if (startDate.getTime() - today.getTime() < 0) {
            errors.dateBeforeToday = true;
        }

        if (
            this.recurring_schedule.end_type === 'repetition_count' &&
            this.recurring_schedule.repetition_count <
                this.lastPaymentBlock.paymentIndex
        ) {
            errors.totalLessThanSent = this.lastPaymentBlock.paymentIndex;
        }

        return errors;
    }

    @action
    mergeBlockAttributes(block, attributes) {
        super.mergeBlockAttributes(block, attributes);
        super.mergeChildrenAttributes(block.children);

        if (block.recurring_schedule) {
            this.recurring_schedule = block.recurring_schedule;
            this.sent_invoices_count = block.sent_invoices_count;
            this.upcoming_invoices_count = block.upcoming_invoices_count;
            this.paid_payments_count = block.paid_payments_count;
            this.total_paid_amount = block.total_paid_amount;
            this.last_invoice_date = block.last_invoice_date;
        }

        if (block.upcoming_invoices) {
            this.upcoming_invoices = block.upcoming_invoices;
        }

        if (block.upcoming_invoice_due_date) {
            this.upcoming_invoice_due_date = block.upcoming_invoice_due_date;
        }
    }

    getDataForServer(): RecurringScheduleBlockDataForServer {
        return {
            ...super.getDataForServer(),
            type: this.type as 'recurring_schedule_block',
            recurring_schedule: this.recurring_schedule,
            sent_invoices_count: this.sent_invoices_count,
            upcoming_invoices_count: this.upcoming_invoices_count,
            paid_payments_count: this.paid_payments_count,
            total_paid_amount: this.total_paid_amount,
            last_invoice_date: this.last_invoice_date
        };
    }
}
